import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useRole } from "@/hooks/use-role";
import { PermissionDeniedPage } from "@/components/team/PermissionDeniedPage";
import { AdminOnly } from "@/components/common/admin-only";
import { Loader2, AlertTriangle, Unplug } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import {
	useEmailSettings,
	useProvisionEmailSettings,
	useUpdateEmailSettings,
	useDisconnectEmailSettings,
} from "@/hooks/use-email-settings";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
	CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_app/_settings/email-settings")({
	component: EmailSettingsPage,
});

function EmailSettingsPage() {
	const { isReadWrite } = useRole();
	if (!isReadWrite) {
		return (
			<PermissionDeniedPage
				requiredRole="Read/Write"
				message="You need Read/Write or Admin access to manage email settings."
			/>
		);
	}
	return <EmailSettingsPageInner />;
}

function EmailSettingsPageInner() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const { data: settings, isLoading } = useEmailSettings(campaignId);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
			</div>
		)
	}

	return (
		<div className="space-y-6 px-6 py-6">
			{settings?.is_provisioned ? (
				<ProvisionedSettingsForm campaignId={campaignId!} />
			) : (
				<SetupForm campaignId={campaignId!} />
			)}
		</div>
	)
}

// ---------------------------------------------------------------------------
// State A: Not Provisioned — Setup Form
// ---------------------------------------------------------------------------

function SetupForm({ campaignId }: { campaignId: number }) {
	const { data: settings } = useEmailSettings(campaignId);
	const provision = useProvisionEmailSettings(campaignId);

	const [fromName, setFromName] = useState(settings?.from_name ?? "");
	const [replyToEmail, setReplyToEmail] = useState("");
	const [disclaimerText, setDisclaimerText] = useState(
		settings?.disclaimer_text ?? "",
	)
	const [physicalAddress, setPhysicalAddress] = useState(
		settings?.physical_address ?? "",
	)

	const handleProvision = () => {
		if (!fromName || !replyToEmail) return;
		provision.mutate({
			from_name: fromName,
			reply_to_email: replyToEmail,
			disclaimer_text: disclaimerText || undefined,
			physical_address: physicalAddress || undefined,
		})
	}

	const canSubmit = fromName && replyToEmail && !provision.isPending;

	return (
		<>
			<Alert>
				<AlertTriangle className="h-4 w-4" />
				<AlertDescription>
					Using platform defaults — set up your sender identity to
					customize how your emails appear.
				</AlertDescription>
			</Alert>

			<Card>
				<CardHeader>
					<CardTitle>Email Settings</CardTitle>
					<CardDescription>
						Configure your sender identity to start sending emails
						from your campaign.
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="space-y-2">
						<Label htmlFor="from-name">From Name</Label>
						<Input
							id="from-name"
							placeholder="Sarah Chen"
							value={fromName}
							onChange={(e) => setFromName(e.target.value)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="reply-to">Reply-To Email</Label>
						<Input
							id="reply-to"
							type="email"
							placeholder="sarah@example.com"
							value={replyToEmail}
							onChange={(e) => setReplyToEmail(e.target.value)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="disclaimer">
							Political Disclaimer
						</Label>
						<Textarea
							id="disclaimer"
							placeholder="Paid for by..."
							value={disclaimerText}
							onChange={(e) => setDisclaimerText(e.target.value)}
							rows={2}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="address">
							Physical Address (CAN-SPAM)
						</Label>
						<Textarea
							id="address"
							placeholder="123 Main St, Springfield, IL 62701"
							value={physicalAddress}
							onChange={(e) =>
								setPhysicalAddress(e.target.value)
							}
							rows={2}
						/>
					</div>
				</CardContent>
				<CardFooter className="border-t pt-6">
					<AdminOnly message="Admin access required to connect a sender"><Button
						onClick={handleProvision}
						disabled={!canSubmit}
					>
						{provision.isPending && (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						)}
						{provision.isPending
							? "Saving..."
							: "Save & Connect"}
					</Button></AdminOnly>
				</CardFooter>
			</Card>
		</>
	)
}

// ---------------------------------------------------------------------------
// State B: Provisioned — Edit Form
// ---------------------------------------------------------------------------

function ProvisionedSettingsForm({ campaignId }: { campaignId: number }) {
	const { isAdmin } = useRole();
	const { data: settings } = useEmailSettings(campaignId);
	const update = useUpdateEmailSettings(campaignId);
	const disconnect = useDisconnectEmailSettings(campaignId);
	const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);

	const [fromName, setFromName] = useState(settings?.from_name ?? "");
	const [replyToEmail, setReplyToEmail] = useState(
		settings?.reply_to_email ?? "",
	)
	const [disclaimerText, setDisclaimerText] = useState(
		settings?.disclaimer_text ?? "",
	)
	const [physicalAddress, setPhysicalAddress] = useState(
		settings?.physical_address ?? "",
	)

	const isDirty =
		fromName !== (settings?.from_name ?? "") ||
		replyToEmail !== (settings?.reply_to_email ?? "") ||
		disclaimerText !== (settings?.disclaimer_text ?? "") ||
		physicalAddress !== (settings?.physical_address ?? "");

	const handleReset = () => {
		setFromName(settings?.from_name ?? "");
		setReplyToEmail(settings?.reply_to_email ?? "");
		setDisclaimerText(settings?.disclaimer_text ?? "");
		setPhysicalAddress(settings?.physical_address ?? "");
	}

	const handleSave = () => {
		update.mutate(
			{
				from_name: fromName || undefined,
				reply_to_email: replyToEmail || undefined,
				disclaimer_text: disclaimerText || undefined,
				physical_address: physicalAddress || undefined,
			},
			{
				onSuccess: (data) => {
					setFromName(data.from_name);
					setReplyToEmail(data.reply_to_email ?? "");
					setDisclaimerText(data.disclaimer_text ?? "");
					setPhysicalAddress(data.physical_address ?? "");
				},
			},
		)
	}

	const syncBadgeVariant =
		settings?.sync_status === "active"
			? "default"
			: settings?.sync_status === "error"
				? "destructive"
				: "secondary";

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle>Email Configuration</CardTitle>
						<CardDescription>
							Manage your sender identity and compliance
							settings.
						</CardDescription>
					</div>
					{settings?.sync_status && (
						<Badge variant={syncBadgeVariant}>
							{settings.sync_status === "active"
								? "Connected"
								: settings.sync_status}
						</Badge>
					)}
				</div>
			</CardHeader>
			<CardContent className="space-y-6">
				<div className="space-y-2">
					<Label htmlFor="edit-from-name">From Name</Label>
					<Input
						id="edit-from-name"
						value={fromName}
						onChange={(e) => setFromName(e.target.value)}
					/>
				</div>

				<div className="space-y-2">
					<Label>From Email</Label>
					<Input value={settings?.from_email ?? ""} disabled />
					<p className="text-xs text-muted-foreground">
						Generated automatically from your name and the platform
						sending domain.
					</p>
				</div>

				<div className="space-y-2">
					<Label htmlFor="edit-reply-to">Reply-To Email</Label>
					<Input
						id="edit-reply-to"
						type="email"
						value={replyToEmail}
						onChange={(e) => setReplyToEmail(e.target.value)}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="edit-disclaimer">
						Political Disclaimer
					</Label>
					<Textarea
						id="edit-disclaimer"
						value={disclaimerText}
						onChange={(e) => setDisclaimerText(e.target.value)}
						rows={2}
					/>
				</div>

				<div className="space-y-2">
					<Label htmlFor="edit-address">
						Physical Address (CAN-SPAM)
					</Label>
					<Textarea
						id="edit-address"
						value={physicalAddress}
						onChange={(e) => setPhysicalAddress(e.target.value)}
						rows={2}
					/>
				</div>
			</CardContent>
			<CardFooter className="flex items-center justify-between border-t pt-6">
				<div className="flex items-center gap-3">
					<Button
						onClick={handleSave}
						disabled={update.isPending || !isDirty}
					>
						{update.isPending && (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						)}
						{update.isPending ? "Saving..." : "Save Changes"}
					</Button>
					{isDirty && (
						<Button variant="ghost" onClick={handleReset}>
							Cancel
						</Button>
					)}
				</div>
				<Dialog open={showDisconnectDialog} onOpenChange={setShowDisconnectDialog}>
					<DialogTrigger asChild>
						<Button variant="outline" className="text-destructive" disabled={!isAdmin}>
							<Unplug className="mr-2 h-4 w-4" />
							Disconnect
						</Button>
					</DialogTrigger>
					<DialogContent>
						<DialogHeader>
							<DialogTitle>Disconnect Email Account?</DialogTitle>
							<DialogDescription>
								This will deactivate your email account
								configuration. You can set it up again
								afterwards.
							</DialogDescription>
						</DialogHeader>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setShowDisconnectDialog(false)}
							>
								Cancel
							</Button>
							<Button
								variant="destructive"
								disabled={disconnect.isPending}
								onClick={() => {
									disconnect.mutate(undefined, {
										onSuccess: () => setShowDisconnectDialog(false),
									});
								}}
							>
								{disconnect.isPending && (
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								)}
								{disconnect.isPending ? "Disconnecting..." : "Disconnect"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</CardFooter>
		</Card>
	)
}
